import React from "react";
import { Link } from "react-router-dom";
import { ChartScatterIcon } from "@phosphor-icons/react";
import EmptyContainer from "../../../shared/EmptyContainer/EmptyContainer";
import { IExerciseHistoryProfile } from "../../../endpoints/profiling";
import styles from "../AthleteExerciseHistoryPage.module.scss";

interface ProfilingHistoryTabProps {
  profiles: IExerciseHistoryProfile[];
  athleteId: string;
}

function formatDate(d?: string): string {
  if (!d) return "—";
  try {
    return new Date(d).toLocaleDateString("it-IT", {
      day: "2-digit",
      month: "2-digit",
      year: "2-digit",
    });
  } catch {
    return d;
  }
}

/**
 * Calcola il verdetto F-V dal valore FVimb (sez.6.2):
 * FVimb ~0% = bilanciato; negativo = deficit velocità; positivo = deficit forza.
 */
function fvVerdetto(fvimb: number): { label: string; className: string } {
  if (fvimb > 10) return { label: "Deficit di forza", className: styles.fvVerdictForza };
  if (fvimb < -10) return { label: "Deficit di velocità", className: styles.fvVerdictVelocita };
  return { label: "Bilanciato", className: styles.fvVerdictBilanciato };
}

/** Ritorna la classe CSS per il badge jumpMode (sez.6.2.1). */
function jumpModeClass(mode?: string): string {
  if (mode === "SJ") return styles.jumpModeSJ;
  if (mode === "CMJ") return styles.jumpModeCMJ;
  return styles.jumpModeOther;
}

/** Raggruppa i profili per jumpMode (sez.6.2.1 — SJ e CMJ non devono essere mischiati). */
function groupByJumpMode(
  profiles: IExerciseHistoryProfile[],
): Map<string | undefined, IExerciseHistoryProfile[]> {
  const map = new Map<string | undefined, IExerciseHistoryProfile[]>();
  for (const p of profiles) {
    const mode: string | undefined = p.outputs?.jumpMode ?? undefined;
    if (!map.has(mode)) map.set(mode, []);
    map.get(mode)!.push(p);
  }
  return map;
}

/** Restituisce true se almeno un profilo ha un campo jumpMode nei propri outputs. */
function hasJumpModeProfiles(profiles: IExerciseHistoryProfile[]): boolean {
  return profiles.some((p) => p.outputs?.jumpMode != null);
}

/** Singola card profilo (riusata per entrambi i layout: raggruppato e generico). */
const ProfileCard: React.FC<{
  p: IExerciseHistoryProfile;
  athleteId: string;
  showJumpMode?: boolean;
}> = ({ p, athleteId, showJumpMode }) => {
  const metricEntries = Object.entries(p.outputs || {})
    .filter(([k, v]) => k !== "jumpMode" && typeof v === "number")
    .slice(0, 6);

  const fvimb =
    typeof p.outputs?.FVimb === "number" ? (p.outputs.FVimb as number) : null;
  const jumpMode: string | undefined = p.outputs?.jumpMode;
  const profileId = p.protocolId;

  return (
    <Link
      key={p._id}
      to={`/dashboard/users/${athleteId}/profiling/${profileId}`}
      className={styles.profileCard}
    >
      <div className={styles.profileCardHeader}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
          {p.profilingHubProtocolId && (
            <span className={styles.profileChip}>{p.profilingHubProtocolId}</span>
          )}
          {/* Badge jumpMode separato per SJ vs CMJ (sez.6.2.1) */}
          {showJumpMode && jumpMode && (
            <span className={`${styles.jumpModeBadge} ${jumpModeClass(jumpMode)}`}>
              {jumpMode}
            </span>
          )}
          <p className={styles.profileCardTitle}>
            {p.protocolName || p.protocolId}
          </p>
        </div>
        <p className={styles.profileCardDate}>{formatDate(p.sessionDate)}</p>
      </div>

      {/* Verdetto F-V (sez.6.2 — solo se FVimb è presente) */}
      {fvimb !== null && (
        <div style={{ marginBottom: 10 }}>
          <span className={`${styles.fvVerdictBadge} ${fvVerdetto(fvimb).className}`}>
            {fvVerdetto(fvimb).label} · FVimb {fvimb > 0 ? "+" : ""}{fvimb.toFixed(1)}%
          </span>
        </div>
      )}

      {metricEntries.length > 0 && (
        <div className={styles.profileMetrics}>
          {metricEntries.map(([k, v]) => (
            <div key={k} className={styles.profileMetricItem}>
              <p className={styles.profileMetricLabel}>{k}</p>
              <p className={styles.profileMetricValue}>
                {typeof v === "number"
                  ? v % 1 === 0
                    ? v
                    : v.toFixed(3).replace(/\.?0+$/, "")
                  : String(v)}
              </p>
            </div>
          ))}
        </div>
      )}
    </Link>
  );
};

/**
 * Sezione Profiling — sez.9.2:
 * - Raggruppa per jumpMode (SJ vs CMJ) se presenti (sez.6.2.1)
 * - Mostra il verdetto F-V da FVimb quando disponibile (sez.6.2)
 */
const ProfilingHistoryTab: React.FC<ProfilingHistoryTabProps> = ({
  profiles,
  athleteId,
}) => {
  if (profiles.length === 0) {
    return (
      <EmptyContainer
        Icon={ChartScatterIcon}
        title="Nessun profilo registrato"
        description="I profili Profiling Hub per questo esercizio appariranno qui."
        addButton={false}
        onClick={() => undefined}
        buttonText=""
      />
    );
  }

  const hasJumpMode = hasJumpModeProfiles(profiles);

  /* Profili F-V salto: raggruppa per jumpMode (SJ / CMJ) — confronti incrociati bloccati in UI */
  if (hasJumpMode) {
    const grouped = groupByJumpMode(profiles);
    const modeOrder = ["SJ", "CMJ"];
    const allModes = [
      ...modeOrder.filter((m) => grouped.has(m)),
      ...[...grouped.keys()].filter((m) => m != null && !modeOrder.includes(m as string)),
      ...(grouped.has(undefined) ? [undefined] : []),
    ] as Array<string | undefined>;

    return (
      <div>
        {allModes.map((mode) => {
          const group = grouped.get(mode) ?? [];
          const modeName = mode ?? "Altro";
          return (
            <div key={String(mode)} className={styles.profilingSubSection}>
              <div className={styles.profilingSubSectionTitle}>
                <span
                  className={`${styles.jumpModeBadge} ${jumpModeClass(mode)}`}
                >
                  {modeName}
                </span>
                <p className={styles.profilingSubSectionLabel}>
                  {mode === "SJ"
                    ? "Squat Jump — concentrico puro"
                    : mode === "CMJ"
                    ? "Counter-Movement Jump — ciclo SSC"
                    : "Profili"}
                </p>
                <span className={styles.sectionCount}>{group.length}</span>
              </div>
              <div className={styles.profilingGrid}>
                {group.map((p) => (
                  <ProfileCard
                    key={p._id}
                    p={p}
                    athleteId={athleteId}
                    showJumpMode={false}
                  />
                ))}
              </div>
            </div>
          );
        })}
      </div>
    );
  }

  /* Profili generici (non F-V salto): griglia normale */
  return (
    <div className={styles.profilingGrid}>
      {profiles.map((p) => (
        <ProfileCard key={p._id} p={p} athleteId={athleteId} showJumpMode />
      ))}
    </div>
  );
};

export default ProfilingHistoryTab;
